import type { ThemeLayout } from './themes';

// Navigation item classes based on theme navStyle
export function getNavItemClasses(
  layout: ThemeLayout,
  isActive: boolean,
  isColored: boolean
): string {
  const base = 'flex items-center gap-3 px-4 py-2.5 transition-all duration-150 cursor-pointer';

  if (isColored) {
    if (isActive) {
      return `${base} ${getNavActiveColored(layout.navStyle)}`;
    }
    return `${base} ${getNavInactiveColored()}`;
  }

  if (isActive) {
    return `${base} ${getNavActiveStandard(layout.navStyle)}`;
  }
  return `${base} ${getNavInactiveStandard(layout.navStyle)}`;
}

function getNavActiveStandard(navStyle: ThemeLayout['navStyle']): string {
  switch (navStyle) {
    case 'highlight':
      return 'bg-primary-50 text-primary-700 rounded-lg font-medium';
    case 'pill':
      return 'bg-primary-100 text-primary-700 rounded-full font-medium';
    case 'indicator':
      return 'text-primary-700 font-medium border-l-[3px] border-primary-500 pl-3.5 -ml-px bg-primary-50/50';
    default:
      return 'bg-primary-50 text-primary-700 rounded-lg font-medium';
  }
}

function getNavInactiveStandard(navStyle: ThemeLayout['navStyle']): string {
  switch (navStyle) {
    case 'highlight':
      return 'text-gray-600 hover:bg-gray-100 rounded-lg';
    case 'pill':
      return 'text-gray-600 hover:bg-gray-100 rounded-full';
    case 'indicator':
      return 'text-gray-500 hover:text-gray-900 border-l-[3px] border-transparent pl-3.5 -ml-px hover:border-gray-200';
    default:
      return 'text-gray-600 hover:bg-gray-100 rounded-lg';
  }
}

function getNavActiveColored(navStyle: ThemeLayout['navStyle']): string {
  switch (navStyle) {
    case 'pill':
      return 'theme-nav-active rounded-full font-medium';
    case 'indicator':
      return 'theme-nav-active border-l-[3px] border-white/60 pl-3.5 -ml-px';
    default:
      return 'theme-nav-active rounded-lg font-medium';
  }
}

function getNavInactiveColored(): string {
  return 'theme-nav-inactive rounded-lg';
}

// Subitem classes (account list in nav)
export function getSubItemClasses(
  _layout: ThemeLayout,
  isActive: boolean,
  isLocked: boolean,
  isColored: boolean
): string {
  const base = 'flex items-center gap-2 w-full px-4 py-2 text-sm transition-colors';

  if (isLocked) {
    return `${base} ${isColored ? 'text-white/30' : 'text-gray-400'} cursor-not-allowed opacity-50`;
  }

  if (isColored) {
    return isActive
      ? `${base} theme-nav-active rounded-lg`
      : `${base} theme-nav-inactive rounded-lg`;
  }

  return isActive
    ? `${base} bg-primary-50 text-primary-700 rounded-lg`
    : `${base} text-gray-600 hover:bg-gray-100 rounded-lg`;
}

// Sidebar container classes
export function getSidebarClasses(layout: ThemeLayout): {
  container: string;
  headerBorder: string;
  brandColor: string;
  versionColor: string;
  logoutClasses: string;
} {
  switch (layout.sidebarStyle) {
    case 'colored':
      return {
        container: 'sidebar-colored',
        headerBorder: 'border-b border-white/10',
        brandColor: 'text-white',
        versionColor: 'text-white/40',
        logoutClasses: 'theme-nav-inactive rounded-lg',
      };
    case 'transparent':
      return {
        container: 'sidebar-transparent',
        headerBorder: 'border-b border-white/20',
        brandColor: 'text-primary-700',
        versionColor: 'text-primary-400/60',
        logoutClasses: 'text-gray-500 hover:text-gray-700 hover:bg-white/40 rounded-lg',
      };
    case 'solid':
    default:
      return {
        container: 'bg-white border-r border-gray-200',
        headerBorder: 'border-b border-gray-100',
        brandColor: 'text-primary-600',
        versionColor: 'text-gray-400',
        logoutClasses: 'text-gray-600 hover:bg-gray-100 rounded-lg',
      };
  }
}

// Header bar classes
export function getHeaderClasses(layout: ThemeLayout): {
  container: string;
  menuButtonClasses: string;
  userMenuButtonClasses: string;
  avatarBg: string;
  avatarText: string;
  nameColor: string;
  chevronColor: string;
  dropdownClasses: string;
} {
  // Floating sidebar → header blends into page background (no bar, no border)
  if (layout.sidebarShape === 'floating') {
    return {
      container: 'bg-transparent',
      menuButtonClasses: 'p-2 rounded-lg hover:bg-white/40 text-gray-600',
      userMenuButtonClasses: 'flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-white/50',
      avatarBg: 'bg-primary-100',
      avatarText: 'text-primary-700 font-medium',
      nameColor: 'text-gray-700',
      chevronColor: 'text-gray-400',
      dropdownClasses: 'absolute right-0 z-20 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1',
    };
  }

  switch (layout.headerStyle) {
    case 'accent':
      return {
        container: 'header-accent',
        menuButtonClasses: 'p-2 rounded-lg hover:bg-white/10 text-white/80',
        userMenuButtonClasses: 'flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10',
        avatarBg: 'bg-white/20',
        avatarText: 'text-white font-medium',
        nameColor: 'text-white/90',
        chevronColor: 'text-white/50',
        dropdownClasses: 'absolute right-0 z-20 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1',
      };
    case 'transparent':
      return {
        container: 'header-transparent',
        menuButtonClasses: 'p-2 rounded-lg hover:bg-white/40 text-gray-600',
        userMenuButtonClasses: 'flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/40',
        avatarBg: 'bg-primary-100/80',
        avatarText: 'text-primary-700',
        nameColor: 'text-gray-700',
        chevronColor: 'text-gray-400',
        dropdownClasses: 'absolute right-0 z-20 mt-2 w-48 bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 py-1',
      };
    case 'standard':
    default:
      return {
        container: 'bg-white border-b border-gray-200',
        menuButtonClasses: 'p-2 rounded-lg hover:bg-gray-100',
        userMenuButtonClasses: 'flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100',
        avatarBg: 'bg-primary-100',
        avatarText: 'text-primary-700',
        nameColor: 'text-gray-700',
        chevronColor: 'text-gray-400',
        dropdownClasses: 'absolute right-0 z-20 mt-2 w-48 bg-white rounded-lg shadow-lg border py-1',
      };
  }
}

// Density-based spacing
export function getDensityClasses(density: ThemeLayout['density']): {
  mainPadding: string;
  mainPaddingDesktop: string;
  navSpacing: string;
} {
  switch (density) {
    case 'compact':
      return {
        mainPadding: 'p-3',
        mainPaddingDesktop: 'lg:p-5',
        navSpacing: 'space-y-0.5',
      };
    case 'spacious':
      return {
        mainPadding: 'p-6',
        mainPaddingDesktop: 'lg:p-10',
        navSpacing: 'space-y-2',
      };
    case 'comfortable':
    default:
      return {
        mainPadding: 'p-4',
        mainPaddingDesktop: 'lg:p-8',
        navSpacing: 'space-y-1',
      };
  }
}
